/**
 * "My Apps" — the landing page after sign-in.
 *
 * Composes:
 *   - Header (brand + identity + log out)
 *   - InvitationsPanel (pending invites, accept inline)
 *   - Apps grid (apps I own + apps I belong to) with a "Create App" button
 *
 * State is intentionally local (no Redux/RTK Query); the few endpoints we
 * call here are simple list/create/accept and the page refetches on each
 * mutating action. Keeps the code obvious and small.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import Header from '../components/Header';
import AppCard from '../components/AppCard';
import CreateAppModal from '../components/CreateAppModal';
import InvitationsPanel from '../components/InvitationsPanel';
import PartnershipCard from '../components/PartnershipCard';
import { useApi } from '../lib/api';
import '../styles/Dashboard.css';

export default function Dashboard() {
  const { isLoaded } = useUser();
  const { isSignedIn } = useAuth();
  const api = useApi();

  const [apps, setApps] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [isPartner, setIsPartner] = useState(false);
  const [pendingPartnerLinks, setPendingPartnerLinks] = useState([]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [appsRes, invsRes, meRes, pendingLinksRes] = await Promise.all([
        api.listApps(),
        api.listMyInvitations(),
        api.me(),
        api.listMyPartnerLinks().catch(() => ({ items: [] })),
      ]);
      setApps(appsRes.apps || []);
      setInvitations(invsRes.items || []);
      setIsPartner(!!meRes.isPartner);
      setPendingPartnerLinks(pendingLinksRes.items || []);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to load your apps',
      );
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    refresh();
  }, [isLoaded, isSignedIn, refresh]);

  // Note: when /api/me first runs for a brand-new user, the backend
  // auto-attaches any pending invitations. So a fresh sign-up may already
  // have apps in this list with zero invitations. Both flows look correct.

  if (!isLoaded) {
    return (
      <div className="page-shell">
        <Header />
        <main className="page-main">
          <p>Loading…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <Header />

      <main className="page-main">
        <h1 className="page-title">My Workspaces</h1>
        <p className="page-subtitle">
          Each workspace is an isolated environment with its own data. You can create as
          many as you like and invite others to join them.
        </p>

        {error && (
          <div className="banner banner-error">
            <strong>Couldn’t load:</strong> {error}{' '}
            <button className="link-btn" onClick={refresh}>Retry</button>
          </div>
        )}

        <InvitationsPanel
          invitations={invitations}
          onAccepted={refresh}
          onDeclined={refresh}
          onError={(msg) => setError(msg)}
        />

        <PartnershipCard
          isPartner={isPartner}
          pendingLinks={pendingPartnerLinks}
          onRefresh={refresh}
          onError={(msg) => setError(msg)}
        />

        <section className="apps-section">
          <div className="section-head">
            <h2 className="section-title">My Workspaces</h2>
            <button className="btn-primary" onClick={() => setCreateOpen(true)}>
              + Create Workspace
            </button>
          </div>

          {loading ? (
            <p className="muted">Loading your apps…</p>
          ) : apps.length === 0 ? (
            <div className="empty-state">
              <p>You don’t have any workspaces yet.</p>
              <button className="btn-primary" onClick={() => setCreateOpen(true)}>
                Create Your First Workspace
              </button>
            </div>
          ) : (
            <>
              <div className="apps-grid">
                {apps.map((app) => (
                  <AppCard key={app.id} app={app} />
                ))}
              </div>
            </>
          )}
        </section>
      </main>

      <CreateAppModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />
    </div>
  );
}
